import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LiveHelpIcon from '@mui/icons-material/LiveHelp';
import useTranslation from '../hooks/useTranslation';
import { useOperationTimeCheck } from '../hooks/useOperationTime';
import { useAddressAffordance } from '../contexts/AddressAffordanceContext';
import { useNavigationAffordance } from '../contexts/NavigationAffordanceContext';
import ServiceClosedModal from './ServiceClosedModal';
import { trackOrdersTabClick, trackAddressTabClick, trackCartTabClick } from '../services/AnalyticsService';

const NavigationBar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [showIconPulse, setShowIconPulse] = useState(false);
  const [showServiceClosedModal, setShowServiceClosedModal] = useState(false);
  const [arrowPosition, setArrowPosition] = useState(60); // 기본값
  
  // 새로운 메뉴 도움 어포던스 상태
  const [showMenuHelpIconPulse, setShowMenuHelpIconPulse] = useState(false);
  
  // 두 번째 어포던스 상태 추가
  const [showSecondMenuHelpIconPulse, setShowSecondMenuHelpIconPulse] = useState(false);
  
  const addressButtonRef = useRef<HTMLDivElement>(null);
  const { checkAndExecute } = useOperationTimeCheck();
  const { 
    showAddressAffordance, 
    showIconPulse: showAddressIconPulse, 
    checkAddressAndExecute,
    isPollingActive,
    affordanceType,
    isSuccessAffordance
  } = useAddressAffordance();
  
  // Navigation affordance context 사용
  const {
    showSupportAffordance,
    setShowSupportAffordance,
    showMenuHelpAffordance,
    setShowMenuHelpAffordance,
    showSecondMenuHelpAffordance,
    setShowSecondMenuHelpAffordance,
  } = useNavigationAffordance();
  
  // 홈 페이지 첫 진입 시 Support 버튼 어포던스 효과
  // useEffect(() => {
  //   const isHomePage = location.pathname === '/' || location.pathname === '/home';
  //   const hasShownAffordance = sessionStorage.getItem('supportAffordanceShown');
    
  //   if (isHomePage && !hasShownAffordance) {
  //     setShowSupportAffordance(true);
  //     sessionStorage.setItem('supportAffordanceShown', 'true');
  //   }
  // }, [location.pathname, setShowSupportAffordance]);
  
  // 스크롤 시 어포던스 해제
  // useEffect(() => {
  //   if (!showSupportAffordance) return;
    
  //   const handleScroll = () => {
  //     setShowSupportAffordance(false);
  //     // 스크롤 시 어포던스가 해제되면 아이콘 펄스 효과 시작
  //     setShowIconPulse(true);
  //     setTimeout(() => {
  //       setShowIconPulse(false);
  //     }, 4500);
  //   };
    
  //   window.addEventListener('scroll', handleScroll, { passive: true });
    
  //   return () => {
  //     window.removeEventListener('scroll', handleScroll);
  //   };
  // }, [showSupportAffordance, setShowSupportAffordance]);
  
  // 현재 활성화된 탭 확인
  const isActive = (path: string) => {
    return location.pathname === path || 
      (path === '/' && (location.pathname === '/' || location.pathname === '/home'));
  };
  
  // 홈으로 이동
  const goToHome = () => {
    const isCurrentlyHome = location.pathname === '/' || location.pathname === '/home';
    
    if (isCurrentlyHome) {
      // 이미 홈 페이지에 있다면 상단으로 스크롤만
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // 다른 페이지에서 홈으로 이동
      navigate('/');
      // 페이지 이동 후 스크롤 (약간의 지연)
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };
  
  // 주문 내역으로 이동
  const goToOrders = () => {
    // GA 이벤트 전송
    trackOrdersTabClick();
    
    const isCurrentlyOrders = location.pathname === '/orders';
    
    if (isCurrentlyOrders) {
      // 이미 주문 페이지에 있다면 상단으로 스크롤만
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // 다른 페이지에서 주문 내역으로 이동
      navigate("/orders");
      // 페이지 이동 후 스크롤 (약간의 지연)
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };
  
  // 장바구니로 이동 (운영시간 체크 + 주소 체크)
  const goToCart = () => {
    // GA 이벤트 전송
    trackCartTabClick();
    
    checkAddressAndExecute(() => {
      checkAndExecute(
        () => navigate('/cart'), // 운영 중일 때
        () => setShowServiceClosedModal(true), // 운영시간 외일 때
        true // Cart 비우기
      );
    }, 'cart');
  };
  
  // 주소 페이지로 이동
  const goToAddress = () => {
    // GA 이벤트 전송
    trackAddressTabClick();
    
    checkAddressAndExecute(() => {
      // 주소가 설정된 경우에만 주소 페이지로 이동
      navigate('/address');
    }, 'address');
  };
  
  // Support 버튼 클릭 - FAQ 페이지로 이동
  const openSupport = () => {
    // 어포던스가 활성화되어 있었다면 펄스 효과 시작
    if (showSupportAffordance) {
      setShowIconPulse(true);
      setTimeout(() => {
        setShowIconPulse(false);
      }, 4500);
    }
    
    if (showMenuHelpAffordance) {
      setShowMenuHelpIconPulse(true);
      setTimeout(() => {
        setShowMenuHelpIconPulse(false);
      }, 4500);
    }
    
    if (showSecondMenuHelpAffordance) {
      setShowSecondMenuHelpIconPulse(true);
      setTimeout(() => {
        setShowSecondMenuHelpIconPulse(false);
      }, 4500);
    }
    
    setShowSupportAffordance(false);
    setShowMenuHelpAffordance(false);
    setShowSecondMenuHelpAffordance(false);
    
    // FAQ 페이지로 이동
    navigate('/faq');
    
    // 페이지 이동 후 상단으로 스크롤
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  // 디버깅용 전역 함수 등록 (개발 환경에서만)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // 기존 어포던스 관련 함수들
      (window as any).resetSupportAffordance = () => {
        sessionStorage.removeItem('supportAffordanceShown');
        setShowSupportAffordance(false);
        setShowIconPulse(false);
      };
      
      (window as any).triggerSupportAffordance = () => {
        setShowSupportAffordance(true);
        setTimeout(() => {
          setShowSupportAffordance(false);
          setShowIconPulse(true);
          setTimeout(() => {
            setShowIconPulse(false);
          }, 4500);
        }, 2000);
      };
      
      (window as any).showSupportAffordanceNow = () => {
        setShowSupportAffordance(true);
      };
      
      (window as any).hideSupportAffordanceNow = () => {
        setShowSupportAffordance(false);
      };
      
      // 메뉴 도움 어포던스 관련 함수들
      (window as any).resetMenuHelpAffordance = () => {
        sessionStorage.removeItem('menuHelpAffordanceShown');
        sessionStorage.removeItem('secondMenuHelpAffordanceShown');
        sessionStorage.removeItem('previousPath');
        localStorage.removeItem('pageNavigationCount');
        localStorage.removeItem('secondMenuHelpCount');
        localStorage.removeItem('hasShownFirstMenuHelp');
        localStorage.removeItem('hasShownSecondMenuHelp');
        setShowMenuHelpAffordance(false);
        setShowMenuHelpIconPulse(false);
        setShowSecondMenuHelpAffordance(false);
        setShowSecondMenuHelpIconPulse(false);
      };
      
      (window as any).triggerMenuHelpAffordance = () => {
        setShowMenuHelpAffordance(true);
        setTimeout(() => {
          setShowMenuHelpAffordance(false);
          setShowMenuHelpIconPulse(true);
          setTimeout(() => {
            setShowMenuHelpIconPulse(false);
          }, 4500);
        }, 2000);
      };
      
      (window as any).triggerSecondMenuHelpAffordance = () => {
        setShowSecondMenuHelpAffordance(true);
        setTimeout(() => {
          setShowSecondMenuHelpAffordance(false);
          setShowSecondMenuHelpIconPulse(true);
          setTimeout(() => {
            setShowSecondMenuHelpIconPulse(false);
          }, 4500);
        }, 2000);
      };
      
      (window as any).showMenuHelpAffordanceNow = () => {
        setShowMenuHelpAffordance(true);
      };
      
      (window as any).hideMenuHelpAffordanceNow = () => {
        setShowMenuHelpAffordance(false);
      };
      
      (window as any).getPageNavigationCount = () => {
        const count = localStorage.getItem('pageNavigationCount') || '0';
        return parseInt(count, 10);
      };
      
      (window as any).setPageNavigationCount = (count: number) => {
        localStorage.setItem('pageNavigationCount', count.toString());
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Address 버튼 위치 계산
  useEffect(() => {
    const calculateArrowPosition = () => {
      if (addressButtonRef.current && showAddressAffordance) {
        const buttonRect = addressButtonRef.current.getBoundingClientRect();
        const windowWidth = window.innerWidth;
        const buttonCenter = buttonRect.left + buttonRect.width / 2;
        const screenCenter = windowWidth / 2;
        const offset = buttonCenter - screenCenter;
        setArrowPosition(offset);
      }
    };

    calculateArrowPosition();
    window.addEventListener('resize', calculateArrowPosition);
    
    return () => {
      window.removeEventListener('resize', calculateArrowPosition);
    };
  }, [showAddressAffordance]);
  
  // 페이지 전환 카운트 및 메뉴 도움 어포던스 로직
  useEffect(() => {
    // 페이지가 완전히 로드된 후에만 실행되도록 지연
    const timer = setTimeout(() => {
    // 이전 페이지 경로를 저장하여 실제 페이지 전환인지 확인
    const previousPath = sessionStorage.getItem('previousPath');
    const currentPath = location.pathname;
    
    // 카운트 대상 경로 정의
    const countableRoutes = ['/guides', '/categories', '/restaurant', '/chains'];
    const isCountablePath = countableRoutes.some(route => 
      currentPath.startsWith(route) || currentPath.includes(route)
    );
    
    // 실제 페이지 전환이고 카운트 대상 경로인 경우에만 카운트 증가
    if (previousPath && previousPath !== currentPath && isCountablePath) {
      const currentCount = parseInt(localStorage.getItem('pageNavigationCount') || '0', 10);
      const newCount = currentCount + 1;
      localStorage.setItem('pageNavigationCount', newCount.toString());
      
      // 장바구니 상태 확인
      const cartData = localStorage.getItem('cart');
      let isCartEmpty = true;
      
      if (cartData) {
        try {
          const cart = JSON.parse(cartData);
          isCartEmpty = !cart || !cart.items || cart.items.length === 0;
        } catch (error) {
          console.error('Cart data parsing error:', error);
          isCartEmpty = true;
        }
      }
      
      // 첫 번째 어포던스 조건 체크
      const shouldShowMenuHelp = isCartEmpty && newCount >= 4;
      const hasShownFirstMenuHelp = localStorage.getItem('hasShownFirstMenuHelp') === 'true';
      const hasShownBasicAffordance = sessionStorage.getItem('supportAffordanceShown');
      
      // 첫 번째 어포던스: 아직 표시하지 않았고 조건을 충족하는 경우
      if (shouldShowMenuHelp && !hasShownFirstMenuHelp && hasShownBasicAffordance) {
        setShowMenuHelpAffordance(true);
        localStorage.setItem('hasShownFirstMenuHelp', 'true'); // 첫 번째 어포던스 표시 기록
        
        // 어포던스 표시 후 상태 초기화 (재조건 달성 시 재표시 가능하도록)
        localStorage.setItem('pageNavigationCount', '0');
      }
        
      // 두 번째 어포던스 로직: 첫 번째 어포던스가 표시된 후 다시 4번 카운트
      const hasShownSecondMenuHelp = localStorage.getItem('hasShownSecondMenuHelp') === 'true';
      if (hasShownFirstMenuHelp && !showMenuHelpAffordance && !showSecondMenuHelpAffordance && !hasShownSecondMenuHelp) {
        const secondMenuHelpCount = parseInt(localStorage.getItem('secondMenuHelpCount') || '0', 10);
        const newSecondCount = secondMenuHelpCount + 1;
        localStorage.setItem('secondMenuHelpCount', newSecondCount.toString());
        
        const shouldShowSecondMenuHelp = isCartEmpty && newSecondCount >= 4;
        
        if (shouldShowSecondMenuHelp) {
          setShowSecondMenuHelpAffordance(true);
          localStorage.setItem('hasShownSecondMenuHelp', 'true'); // 두 번째 어포던스 표시 기록
          
          // 두 번째 어포던스 표시 후 카운트 초기화
          localStorage.setItem('secondMenuHelpCount', '0');
        }
      }
    }
    
    // 현재 페이지 경로를 저장
    sessionStorage.setItem('previousPath', currentPath);
    }, 100); // 100ms 지연으로 페이지 로딩 완료 대기
    
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, showMenuHelpAffordance, showSecondMenuHelpAffordance]);
  
  // 메뉴 도움 어포던스 스크롤 시 해제
  useEffect(() => {
    if (!showMenuHelpAffordance && !showSecondMenuHelpAffordance) return;
    
    const handleScroll = () => {
      if (showMenuHelpAffordance) {
        setShowMenuHelpAffordance(false);
        // 첫 번째 메뉴 도움 어포던스가 스크롤로 해제되면 아이콘 펄스 효과 시작
        setShowMenuHelpIconPulse(true);
        setTimeout(() => {
          setShowMenuHelpIconPulse(false);
        }, 4500);
      }
      
      if (showSecondMenuHelpAffordance) {
        setShowSecondMenuHelpAffordance(false);
        // 두 번째 메뉴 도움 어포던스가 스크롤로 해제되면 아이콘 펄스 효과 시작
        setShowSecondMenuHelpIconPulse(true);
        setTimeout(() => {
          setShowSecondMenuHelpIconPulse(false);
        }, 4500);
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMenuHelpAffordance, showSecondMenuHelpAffordance]);
  
  return (
    <>
      <div
        data-app-bottom-navigation
        className="fixed bottom-0 left-1/2 transform -translate-x-1/2 md-navigation-bar border-t md-outline z-[9999] max-w-mobile w-full"
      >
        <div className="w-full">
          <div className="grid grid-cols-5 py-2 px-2">
            <div 
              className="flex flex-col items-center justify-center cursor-pointer py-1 min-h-[48px]" 
              onClick={goToHome}
            >
              {/* Home Icon */}
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" 
                className={`w-6 h-6 ${isActive('/') ? 'text-red-600' : 'text-gray-500'}`}>
                <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
              </svg>
              <span className={`md-label-small mt-1 ${isActive('/') ? 'text-red-600 font-medium' : 'text-gray-500'}`}>{t('nav.home')}</span>
            </div>
            
            <div 
              className="flex flex-col items-center justify-center cursor-pointer py-1 min-h-[48px]" 
              onClick={goToOrders}
            >
              {/* Orders Icon */}
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" 
                className={`w-6 h-6 ${isActive('/orders') ? 'text-red-600' : 'text-gray-500'}`}>
                <path d="M7 2h10a2 2 0 012 2v16l-3-1.5L13 20l-3-1.5L7 20V4a2 2 0 012-2zm2 5h6V5H9v2zm0 4h6V9H9v2zm0 4h4v-2H9v2z" />
              </svg>
              <span className={`md-label-small mt-1 ${isActive('/orders') ? 'text-red-600 font-medium' : 'text-gray-500'}`}>{t('nav.orders')}</span>
            </div>
            
            <div 
              className={`flex flex-col items-center justify-center cursor-pointer py-1 min-h-[48px] relative ${
                showSupportAffordance || showMenuHelpAffordance || showSecondMenuHelpAffordance ? 'support-affordance' : ''
              } ${showIconPulse || showMenuHelpIconPulse || showSecondMenuHelpIconPulse ? 'support-icon-pulse' : ''}`}
              onClick={openSupport}
            >
              {/* Support 툴팁 - 기존 어포던스 */}
              {/* {showSupportAffordance && (
                <div 
                  className="support-tooltip"
                  dangerouslySetInnerHTML={{ __html: t('nav.support.affordance') }}
                />
              )} */}
              
              {/* Support 툴팁 - 메뉴 도움 어포던스 */}
              {/* {showMenuHelpAffordance && (
                <div 
                  className="support-tooltip"
                  dangerouslySetInnerHTML={{ __html: t('nav.support.menu_help_affordance') }}
                />
              )} */}
              
              {/* Support 툴팁 - 두 번째 메뉴 도움 어포던스 */}
              {/* {showSecondMenuHelpAffordance && (
                <div 
                  className="support-tooltip"
                  dangerouslySetInnerHTML={{ __html: t('nav.support.second_menu_help_affordance') }}
                />
              )} */}
              
              {/* Support Icon */}
              <LiveHelpIcon className="w-6 h-6 text-gray-500 hover:text-red-600 transition-colors" />
              <span className="md-label-small mt-1 text-gray-500 hover:text-red-600 transition-colors">FAQ</span>
            </div>
            
            <div 
              ref={addressButtonRef}
              className={`flex flex-col items-center justify-center cursor-pointer py-1 min-h-[48px] relative ${
                showAddressAffordance ? 'address-affordance' : ''
              } ${showAddressIconPulse ? 'address-icon-pulse' : ''}`}
              onClick={goToAddress}
            >
              {/* Address Icon */}
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" 
                className={`w-6 h-6 ${isActive('/address') ? 'text-red-600' : 'text-gray-500'}`}>
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
              </svg>
              <span className={`md-label-small mt-1 ${isActive('/address') ? 'text-red-600 font-medium' : 'text-gray-500'}`}>{t('nav.address')}</span>
            </div>
            
            <div 
              className="flex flex-col items-center justify-center cursor-pointer py-1 min-h-[48px]" 
              onClick={goToCart}
            >
              {/* Cart Icon */}
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" 
                className={`w-6 h-6 ${isActive('/cart') ? 'text-red-600' : 'text-gray-500'}`}>
                <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z" />
              </svg>
              <span className={`md-label-small mt-1 ${isActive('/cart') ? 'text-red-600 font-medium' : 'text-gray-500'}`}>{t('nav.cart')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Address 어포던스 툴팁 */}
      {showAddressAffordance && (
        <div 
          className="address-tooltip"
          style={{
            '--arrow-offset': `${arrowPosition}px`
          } as React.CSSProperties}
          dangerouslySetInnerHTML={{ 
            __html: (() => {
              if (isSuccessAffordance) {
                return t('nav.address.affordance.success');
              } else if (isPollingActive) {
                return affordanceType === 'cart' 
                  ? t('nav.cart.affordance.polling')
                  : t('nav.address.affordance.polling');
              } else {
                return t('nav.address.affordance');
              }
            })()
          }}
        />
      )}

      {/* 운영시간 외 모달 */}
      <ServiceClosedModal 
        isOpen={showServiceClosedModal}
        onClose={() => setShowServiceClosedModal(false)}
      />
    </>
  );
};

export default NavigationBar;
